<template>
  <div class="org-tree-select">
    <div class="org-tree-toolbar">
      <el-input
        v-model="filterText"
        size="mini"
        placeholder="输入组织名称过滤"
        prefix-icon="el-icon-search"
        clearable
        @input="onFilter"
        @clear="onFilter" />
      <div class="org-tree-actions">
        <el-checkbox v-model="multiSelect" @change="onMultiSelectChange">多选</el-checkbox>
        <el-checkbox v-model="includeChildren" class="org-tree-include-children" @change="onIncludeChildrenChange">含下级</el-checkbox>
      </div>
    </div>
    <vxe-table
      ref="orgTable"
      class="org-tree-table"
      height="100%"
      :data="treeData"
      :tree-config="treeConfig"
      :checkbox-config="checkboxConfig"
      :show-header="false"
      :loading="loading"
      @cell-click="onCellClick"
      @checkbox-change="onCheckboxChange"
      @checkbox-all="onCheckboxAll">
      <vxe-column type="checkbox" width="40" align="center"></vxe-column>
      <vxe-column tree-node min-width="240">
        <template #default="{ row }">
          <span class="org-node-name" :class="{ 'is-active': `${row.OrganizationId}` === `${currentId}` }">{{ row.OrganizationName }}</span>
        </template>
      </vxe-column>
    </vxe-table>
  </div>
</template>

<script>
export default {
  name: 'yblOrgTreeSelect',
  props: {
    // 当前勾选的车险组织ID,用于回显
    checkedIds: {
      type: Array,
      default: () => []
    },
    // 当前组织主键,用于高亮
    currentId: {
      type: [String, Number],
      default: () => ''
    }
  },
  data () {
    return {
      loading: false,
      filterText: '',
      // 多选开关,默认开启;关闭时为单选(点击行只选中当前行)
      multiSelect: true,
      // 含下级:勾选组织时是否连带其全部子孙组织的成员,默认开启
      includeChildren: true,
      // 完整组织扁平数据(DFS 序,与树节点是同一对象引用,带 pId/level/children)
      originData: [],
      // 全量节点索引(OrganizationId -> node)与父子关系表(pId -> children)。
      // 一次建立,不随过滤/子树重建而改变,供「含下级」收集子孙与回显时还原顶层勾选使用。
      nodeMap: new Map(),
      childrenMap: new Map(),
      treeData: [],
      treeConfig: {
        // 不使用 transform 自动建树,改为前端显式构建带 children 的树。
        // 理由:接口返回的 ParentTeamId 与 ParentId 是两套层级指针(机构用 ParentId、
        // 组织用 ParentTeamId,且存在 43 条两者不一致的记录),交给 vxe-table
        // transform(基于 pId 的 parentField 自动重组)建树时,展开重渲染会误隐藏无关节点。
        // 显式建树后,树的父子关系在数据装载时一次性确定,展开/折叠只切换该节点自身子树的
        // 可见性,不再经过 transform 重排,因此不会误隐藏任何无关节点。
        rowField: 'OrganizationId',
        indent: 14
      },
      checkboxConfig: {
        // 父子节点勾选互不影响,勾选集合即勾选节点的车险组织ID
        checkStrictly: true
      }
    }
  },
  watch: {
    checkedIds: {
      handler (val) {
        this.setChecked(val || [])
      },
      deep: true
    }
  },
  created () {
    this.loadOrganization()
  },
  methods: {
    loadOrganization () {
      this.loading = true
      return this.$store.dispatch('sysBranchOrgQueryOrgInfoAndStatistics', {}).then(data => {
        this.loading = false
        const list = data.datas || []
        // 初始化扁平节点:去重 + 确定父指针 pId(ParentTeamId 优先,与 orgTreePanel 的 initTreeData 同一口径)
        this.originData = this.initOrgNodes(list)
        // 显式构建 children 树:父子关系一次确定,不再交给 vxe-table transform 自动建树
        this.treeData = this.buildOrgTree()
        // 求 DFS 序 + 层级(与树节点同一对象引用,保证 setTreeExpand/setCheckboxRow 可命中)
        this.originData = this.flattenTree(this.treeData)
        // 建立全量节点索引与父子关系表(过滤后仍保持完整,供含下级/回显使用)
        this.buildRelationMaps()
        this.$nextTick(() => {
          this.setChecked(this.checkedIds)
          // 默认展开到第 3 级(根节点算第 1 级)
          this.resetTreeExpand(3)
        })
        this.$emit('loaded', this.originData)
        return this.treeData
      }).catch(() => {
        this.loading = false
      })
    },
    // 扁平列表 -> 去重后的节点对象(带 pId 与 children)
    initOrgNodes (list) {
      const map = new Map()
      const nodes = []
      ;(list || []).forEach(item => {
        const id = `${item.OrganizationId}`
        if (id && !map.has(id)) {
          const node = {
            ...item,
            pId: item.ParentTeamId || item.ParentId,
            children: []
          }
          map.set(id, node)
          nodes.push(node)
        }
      })
      return nodes
    },
    // 建立全量节点索引(nodeMap)与父子关系表(childrenMap)。
    // 独立于 treeData 的子树重建(过滤时 children 会被重挂),保证任意时刻都能拿到完整子孙。
    buildRelationMaps () {
      this.nodeMap = new Map()
      this.childrenMap = new Map()
      this.originData.forEach(node => {
        this.nodeMap.set(`${node.OrganizationId}`, node)
        const pid = `${node.pId}`
        if (pid && pid !== `${node.OrganizationId}`) {
          if (!this.childrenMap.has(pid)) {
            this.childrenMap.set(pid, [])
          }
          this.childrenMap.get(pid).push(node)
        }
      })
    },
    // 递归收集某组织及其全部子孙组织的车险组织ID(含下级开启时使用)。
    // 支持排除个别下级:子树中已被用户取消勾选的节点(不在当前勾选集 selection)不再收集,
    // 这样「含下级」勾选父级后再单独取消某个下级,该下级的成员会从查询范围中排除、列表数据随之变化。
    collectCxOrgIds (org) {
      const ids = []
      const visited = new Set()
      const table = this.$refs.orgTable
      const selection = (table && table.selection) || []
      const stack = [org]
      while (stack.length) {
        const node = stack.pop()
        const key = `${node.OrganizationId}`
        if (visited.has(key)) continue
        visited.add(key)
        // 含下级收集:仅收集仍处于勾选集合中的节点;被手动取消的下级节点剪枝(连同其整棵被连带取消的子树)
        if (node !== org && selection.indexOf(node) === -1) continue
        const cxId = node.CXOrganizationId
        if (cxId != null && cxId !== 0 && ids.indexOf(cxId) === -1) {
          ids.push(cxId)
        }
        const children = this.childrenMap.get(key) || []
        for (let i = children.length - 1; i >= 0; i--) {
          stack.push(children[i])
        }
      }
      return ids
    },
    // 显式构建 children 树:父指针按 pId 挂接;父节点缺失/指向自身的节点作为根;
    // 防御环导致的不可达节点,提升为根避免丢失。返回根节点数组。
    buildOrgTree (scope) {
      const list = scope || this.originData
      const nodeMap = new Map()
      const nodes = []
      list.forEach(node => {
        const id = `${node.OrganizationId}`
        if (id && !nodeMap.has(id)) {
          node.children = []
          nodeMap.set(id, node)
          nodes.push(node)
        }
      })
      nodes.forEach(node => {
        const pid = `${node.pId}`
        if (pid && pid !== `${node.OrganizationId}`) {
          const parent = nodeMap.get(pid)
          if (parent && parent.children.indexOf(node) === -1) {
            parent.children.push(node)
          }
        }
      })
      const roots = nodes.filter(node => {
        const pid = `${node.pId}`
        return !(pid && pid !== `${node.OrganizationId}` && nodeMap.get(pid))
      })
      // 防御:若存在环导致部分节点未从根可达,提升为根,避免数据丢失
      const visited = new Set()
      const collectReachable = (arr) => {
        arr.forEach(node => {
          if (visited.has(node)) return
          visited.add(node)
          collectReachable(node.children)
        })
      }
      collectReachable(roots)
      nodes.forEach(node => {
        if (!visited.has(node)) {
          roots.push(node)
        }
      })
      return roots
    },
    // 树 -> DFS 序扁平列表(与树节点同一对象引用),并计算 level/hasLeaf
    flattenTree (roots) {
      const flat = []
      const visited = new Set()
      const walk = (arr, level) => {
        arr.forEach(node => {
          if (visited.has(node)) return
          visited.add(node)
          node.level = level
          node.hasLeaf = node.children.length > 0
          flat.push(node)
          walk(node.children, level + 1)
        })
      }
      walk(roots, 0)
      return flat
    },
    onFilter () {
      const text = (this.filterText || '').trim()
      if (!text) {
        this.treeData = this.buildOrgTree()
        this.originData = this.flattenTree(this.treeData)
        this.$nextTick(() => this.resetTreeExpand(3))
        return
      }
      const hit = new Set()
      this.originData.forEach(item => {
        if ((item.OrganizationName || '').indexOf(text) > -1) hit.add(`${item.OrganizationId}`)
      })
      // 收集匹配节点的祖先链(按 pId),保证过滤后树结构完整
      let changed = true
      while (changed) {
        changed = false
        this.originData.forEach(item => {
          if (hit.has(`${item.OrganizationId}`)) {
            const pid = `${item.pId}`
            if (pid && !hit.has(pid)) {
              hit.add(pid)
              changed = true
            }
          }
        })
      }
      // 基于命中节点(含祖先链)重建子树,保留原有层级关系
      const subset = this.originData.filter(item => hit.has(`${item.OrganizationId}`))
      this.treeData = this.buildOrgTree(subset)
      // 过滤结果全部展开,保证命中的深层节点可见
      this.$nextTick(() => {
        const table = this.$refs.orgTable
        if (table) {
          table.setAllTreeExpand(true)
        }
      })
    },
    // 点击行任意区域点选/取消(checkbox 列与展开箭头除外)
    onCellClick ({ row, triggerCheckbox, triggerTreeNode }) {
      // 点击 checkbox 本体已由 vxe 处理勾选,避免重复切换
      if (triggerCheckbox || triggerTreeNode) return
      if (this.multiSelect) {
        const table = this.$refs.orgTable
        if (!table) return
        const checked = !table.isCheckedByCheckboxRow(row)
        table.setCheckboxRow(row, checked)
        // 含下级:点选父级时,下级一并勾选/取消
        if (this.includeChildren) {
          this.setSubtreeChecked(row, checked)
        }
      } else {
        this.selectOnly(row)
      }
      this.emitChange()
    },
    // 单选:只勾选当前行,取消其他行
    selectOnly (row) {
      const table = this.$refs.orgTable
      if (!table) return
      const others = (table.getCheckboxRecords() || []).filter(item => item !== row)
      if (others.length) {
        table.setCheckboxRow(others, false)
      }
      table.setCheckboxRow(row, true)
      // 含下级:单选模式同样连带勾选子孙
      if (this.includeChildren) {
        this.setSubtreeChecked(row, true)
      }
    },
    onCheckboxChange ({ row, checked } = {}) {
      // 含下级:勾选/取消父级时,连带勾选/取消其全部子孙(视觉与查询一致)
      if (this.includeChildren && row) {
        this.setSubtreeChecked(row, checked)
      }
      // 单选模式下,直接勾选 checkbox 也约束为单选
      if (!this.multiSelect && checked && row) {
        this.selectOnly(row)
      }
      this.emitChange()
    },
    onCheckboxAll () {
      // 单选模式下,全选只保留第一个
      if (!this.multiSelect) {
        const table = this.$refs.orgTable
        const records = (table && table.getCheckboxRecords()) || []
        if (records.length > 1) {
          this.selectOnly(records[0])
        }
      }
      this.emitChange()
    },
    onMultiSelectChange (val) {
      // 切到单选时,若当前勾选超过 1 个,保留第一个,取消其余
      if (!val) {
        const table = this.$refs.orgTable
        const records = (table && table.getCheckboxRecords()) || []
        if (records.length > 1) {
          this.selectOnly(records[0])
          this.emitChange()
        }
      }
    },
    // 收集某节点的全部子孙节点(经 childrenMap,含未展开节点),供「含下级」勾选联动使用
    collectDescendants (node) {
      const list = []
      const visited = new Set([`${node.OrganizationId}`])
      const stack = [...(this.childrenMap.get(`${node.OrganizationId}`) || [])]
      while (stack.length) {
        const child = stack.pop()
        const key = `${child.OrganizationId}`
        if (visited.has(key)) continue
        visited.add(key)
        list.push(child)
        const grand = this.childrenMap.get(key) || []
        for (let i = grand.length - 1; i >= 0; i--) {
          stack.push(grand[i])
        }
      }
      return list
    },
    // 含下级:勾选/取消某节点时,连带勾选/取消其全部子孙(视觉与查询一致)
    setSubtreeChecked (row, checked) {
      const table = this.$refs.orgTable
      if (!table) return
      const descendants = this.collectDescendants(row)
      if (descendants.length) {
        // setCheckboxRow 只改勾选状态、不触发 checkbox-change 事件,不会递归;
        // 子孙(含折叠中的)一并进入选中集合,查询口径与视觉一致
        table.setCheckboxRow(descendants, checked)
        // 视觉联动:勾选时展开父级本身,让直接下级立即可见且呈勾选态,证明「含下级」已连带;
        // 更深层级保持折叠,用户手动展开时同样已勾选;取消勾选不折叠,保留手动展开状态
        if (checked) {
          table.setTreeExpand([row], true)
        }
      }
    },
    // 切换「含下级」:开启时把已有勾选组织的子孙一并勾选;关闭时保留各节点自身勾选。
    // 之后按新开关状态重新计算查询范围并刷新列表。
    onIncludeChildrenChange () {
      const table = this.$refs.orgTable
      const records = (table && table.getCheckboxRecords()) || []
      if (this.includeChildren && records.length) {
        records.forEach(row => this.setSubtreeChecked(row, true))
      }
      this.emitChange()
    },
    // 收集当前勾选节点的车险组织ID(含下级开启时,附带该组织全部子孙组织的车险组织ID)
    collectChecked () {
      const table = this.$refs.orgTable
      const records = (table && table.getCheckboxRecords()) || []
      const list = []
      records.forEach(row => {
        if (this.includeChildren) {
          this.collectCxOrgIds(row).forEach(cxId => {
            if (list.indexOf(cxId) === -1) list.push(cxId)
          })
        } else {
          const cxId = row.CXOrganizationId
          if (cxId != null && cxId !== 0 && list.indexOf(cxId) === -1) {
            list.push(cxId)
          }
        }
      })
      return list
    },
    // 抛出勾选结果与「含下级」开关状态:
    // 第二个参数即后端查询参数 includeSub(true 含下级 / false 不含)。组织树勾选联动仍在前端
    // 做视觉与ID收集,后端据 includeSub 决定是否按归属机构层级补齐下级数据;关闭时后端只查所选机构。
    emitChange () {
      this.$emit('change', this.collectChecked(), this.includeChildren)
    },
    // 供父组件读取当前「含下级」状态(如重置筛选后与表单默认值对齐)
    getIncludeChildren () {
      return this.includeChildren
    },
    // 重置展开:先全部折叠,再展开到第 level 级(根节点算第 1 级,level 为 0 基)
    resetTreeExpand (level) {
      const table = this.$refs.orgTable
      if (!table) return
      table.setAllTreeExpand(false).then(() => {
        // originData 为树的 DFS 序扁平列表(与树节点同一对象引用),按 level 展开前 level-1 层
        const rows = (this.originData || []).filter(item => item.level < level - 1)
        if (rows.length) {
          table.setTreeExpand(rows, true)
        }
      })
    },
    // 从「勾选组织+子孙」的并集中还原顶层勾选:仅保留其祖先不在列表内的节点,
    // 避免含下级开启时,emit 出的子孙车险组织ID经回显(checkedIds)被连带勾选进树里。
    reduceToTopLevel (list) {
      const set = new Set((list || []).map(id => `${id}`))
      const result = []
      this.nodeMap.forEach(node => {
        const cxId = `${node.CXOrganizationId}`
        if (!set.has(cxId)) return
        let hasAncestor = false
        const seen = new Set()
        let pid = `${node.pId}`
        while (pid && pid !== `${node.OrganizationId}` && !seen.has(pid)) {
          seen.add(pid)
          const parent = this.nodeMap.get(pid)
          if (!parent) break
          if (set.has(`${parent.CXOrganizationId}`)) {
            hasAncestor = true
            break
          }
          pid = `${parent.pId}`
        }
        if (!hasAncestor) result.push(node.CXOrganizationId)
      })
      return result
    },
    // 外部重置/回显:按车险组织ID设置勾选
    setChecked (cxIdList) {
      const table = this.$refs.orgTable
      if (!table) return
      // 含下级开启时,checkedIds 为「勾选组织+子孙」的并集,先还原为顶层勾选再应用
      const source = this.includeChildren ? this.reduceToTopLevel(cxIdList || []) : (cxIdList || [])
      const list = source.map(id => `${id}`)
      const current = (table.getCheckboxRecords() || []).map(row => `${row.CXOrganizationId}`)
      const rows = this.originData || []
      const toCheck = rows.filter(row => list.indexOf(`${row.CXOrganizationId}`) > -1 && current.indexOf(`${row.CXOrganizationId}`) === -1)
      const toUncheck = rows.filter(row => list.indexOf(`${row.CXOrganizationId}`) === -1 && current.indexOf(`${row.CXOrganizationId}`) > -1)
      if (toCheck.length) {
        table.setCheckboxRow(toCheck, true)
        // 含下级:回显顶层勾选时,子孙一并勾选,保持视觉与查询范围一致
        if (this.includeChildren) {
          toCheck.forEach(row => this.setSubtreeChecked(row, true))
        }
      }
      if (toUncheck.length) {
        table.setCheckboxRow(toUncheck, false)
      }
    },
    // 树数据重载
    reload () {
      return this.loadOrganization()
    }
  }
}
</script>

<style lang="less" scoped>
.org-tree-select {
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.org-tree-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 8px 8px 6px;
  border-bottom: 1px solid #e8e8e8;
  .el-input {
    flex: 1;
    min-width: 0;
  }
}

.org-tree-actions {
  flex-shrink: 0;
  margin-left: 12px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  .el-checkbox {
    margin-right: 0;
  }
  .org-tree-include-children {
    margin-left: 16px;
  }
}

.org-tree-table {
  flex: 1;
  min-height: 0;
}

.org-node-name {
  &.is-active {
    color: #409eff;
    font-weight: 600;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/OrgTreeSelect.vue